import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Skeleton } from '@/components/ui/skeleton';
import { KbSizeIndicator } from '@/components/onboarding/kb-form/KbSizeIndicator';
import { useOnboardingState } from '@/contexts/OnboardingContext';
import {
  ENRICHMENT_CARDS,
  completeness,
  deriveRecommendations,
  meterProgress,
  orderCards,
  type EnrichmentCardId,
} from '@/lib/enrichment';

import { EnrichmentCard } from './EnrichmentCard';

/**
 * F036 / F052 / F053 — The home enrichment surface.
 *
 * - `disabled=true` (setup phase): renders a faded preview of all 7 cards
 *   without needing `kbProfile`. No meter, no sandbox.
 * - `disabled=false` (default, live phase): full interactive surface with
 *   meter and responsive card grid. Sandbox removed in F053 (the timeline's
 *   Preview step is the canonical test surface).
 */
interface EnrichmentSurfaceProps {
  disabled?: boolean;
}

export function EnrichmentSurface({ disabled = false }: EnrichmentSurfaceProps) {
  const { t } = useTranslation();
  const { kbProfile } = useOnboardingState();
  // F069 #25 — freeze the card order on the first render with a profile so a
  // just-filled card keeps its grid position (check appears in place, no
  // reflow). Reordering happens on the next visit to the page.
  const frozenOrderRef = useRef<EnrichmentCardId[] | null>(null);

  // Disabled preview mode: render all cards faded without kbProfile requirement.
  if (disabled) {
    return (
      <div className="space-y-6" data-testid="enrichment-surface">
        <h2 className="text-lg font-semibold text-muted-foreground">
          {t('bot.enrich.surface_title')}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {ENRICHMENT_CARDS.map((id) => (
            <EnrichmentCard
              key={id}
              id={id}
              disabled
              recommended={false}
              profile={kbProfile ?? undefined}
            />
          ))}
        </div>
      </div>
    );
  }

  // Live mode: skeleton until kbProfile loads.
  if (!kbProfile) {
    return (
      <div className="space-y-4" data-testid="enrichment-surface-loading">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-3 w-full" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {ENRICHMENT_CARDS.map((id) => (
            <Skeleton key={id} className="h-40" />
          ))}
        </div>
      </div>
    );
  }

  const { filled, total, pct } = completeness(kbProfile);
  const recommended = deriveRecommendations(kbProfile);
  if (!frozenOrderRef.current) frozenOrderRef.current = orderCards(kbProfile);
  const orderedCards = frozenOrderRef.current;
  const recommendedSet = new Set(recommended);

  // F174 FR2 — "Bot {{pct}}% configurado — adicione {{next1}} e {{next2}}
  // para chegar a {{nextPct}}%", naming the first two unfilled cards in
  // IMPACT_ORDER order. Degrades gracefully to a singular form with 1 card
  // left, and to a plain "tudo pronto" form once nothing remains.
  const { next, nextPct } = meterProgress(kbProfile);
  const meterLabel =
    next.length >= 2
      ? t('bot.enrich.meter_progress', {
          pct,
          next1: t(`bot.enrich.${next[0]}.title`),
          next2: t(`bot.enrich.${next[1]}.title`),
          nextPct,
        })
      : next.length === 1
        ? t('bot.enrich.meter_progress_one', {
            pct,
            next1: t(`bot.enrich.${next[0]}.title`),
            nextPct,
          })
        : t('bot.enrich.meter_label', { pct });

  return (
    <div className="space-y-6" data-testid="enrichment-surface">
      {/* Title + meter */}
      <div className="space-y-3">
        <h2 className="text-lg font-semibold">{t('bot.enrich.surface_title')}</h2>
        <div className="space-y-1.5">
          <div
            className="h-1.5 w-full rounded-full bg-muted overflow-hidden"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={total}
            aria-valuenow={filled}
            aria-label={meterLabel}
            data-testid="enrichment-meter"
          >
            <div
              className="h-full bg-foreground transition-all duration-300"
              style={{ width: `${pct}%` }}
            />
          </div>
          <p className="text-xs text-muted-foreground">{meterLabel}</p>
        </div>
      </div>

      {/* Responsive card grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {orderedCards.map((id) => (
          <EnrichmentCard
            key={id}
            id={id}
            profile={kbProfile}
            recommended={recommendedSet.has(id)}
          />
        ))}
      </div>

      {/* F074 — context-budget footer: always visible, fed by the backend's
          real knowledge_size; each editor save returns the refreshed profile
          so the value updates without a second GET. */}
      <KbSizeIndicator size={kbProfile.knowledge_size ?? null} />
    </div>
  );
}
